import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppNavigation } from '../../../../hooks/useAppNavigation';
import { useCart } from '../../../../hooks/useCart';
import { useMenus } from '../../../../hooks/useMenus';
import { useMobile } from '../../../../hooks/useMobile';

import { ReactComponent as ArrowLeftIcon } from '../../../../icons/arrow-left.svg';
import { ReactComponent as CartIcon } from '../../../../icons/cart.svg';
import { ReactComponent as ConsumeIcon } from '../../../../icons/consume.svg';
import { ReactComponent as FlameIcon } from '../../../../icons/flame.svg';
import { ReactComponent as FreshIcon } from '../../../../icons/fresh.svg';
import { ReactComponent as FrozenIcon } from '../../../../icons/frozen.svg';

import './page-menu-item.scss';

import { trackEvent } from '../../../../utils/analytics/metaPixel';
import { Currency } from '../../../../utils/currency/Currency';
import { getColorForKeyword } from '../../../../utils/tools';
import { Button } from '../../../common/button/Button';
import { EmptyState } from '../../../empty-state/EmptyState';
import PageLoader from '../../../loader/PageLoader';
import { MenuItemOptionsModal } from '../../../modal/menu-item-options/MenuItemOptionsModal';
import Navigation from '../../../navigation/Navigation';
import { QuantitySelector } from '../../../quantity-selector/QuantitySelector';
import { Sidebar } from '../../../sidebar/Sidebar';

export const MenuItemPage = () => {
  const { menuItemId: id } = useParams();
  const isMobile = useMobile();

  const { menus, menusLoading } = useMenus();
  const menu = useMemo(() => {
    return menus.flatMap((m) => m.menuItems).find((item) => item.id === id);
  }, [menus, id]);

  const { setShowMealChoiceModal } = useAppNavigation();
  const { addToCart, cart, isItemInCart, incrementItem, decrementItem } = useCart();
  const navigate = useNavigate();
  const [showOptionsModal, setShowOptionsModal] = useState(false);

  const hasOptions = menu?.optionGroups?.length > 0;

  useEffect(() => {
    if (!menu) return;
    trackEvent('ViewContent', {
      content_ids: [menu.id],
      content_name: menu.name,
      content_type: 'product',
      value: menu.price ?? 0,
      currency: 'ZAR'
    });
    //eslint-disable-next-line
  }, [menu?.id]);

  const cartItem = useMemo(() => {
    return cart.items.find((cartItem) => cartItem?.id === menu?.id);
  }, [cart, menu]);

  const isInCart = useMemo(() => {
    if (!menu) return;

    return isItemInCart(menu);
    //eslint-disable-next-line
  }, [cart, menu]);

  const handleAddToCartClick = (e) => {
    e.stopPropagation();
    if (hasOptions) {
      setShowOptionsModal(true);
    } else {
      addToCart(menu);
    }
  };

  const handleCreateMealClick = (e) => {
    e.stopPropagation();
    setShowMealChoiceModal(true);
  };

  const NutritionIcons = () => (
    <div className="page-menu-item__nutrition-icons">
      <div className="page-menu-item__nutrition-icon">
        <FrozenIcon />
        <span>Keep refrigerated at 4°C or below</span>
      </div>
      <div className="page-menu-item__nutrition-icon">
        <ConsumeIcon />
        <span>Consume within 4 days of delivery</span>
      </div>
      <div className="page-menu-item__nutrition-icon">
        <FreshIcon />
        <span>Meals are delivered fresh and not frozen</span>
      </div>
    </div>
  );

  if (!menu) {
    return <PageLoader loading={menusLoading} />;
  }

  return (
    <Navigation>
      <div className="page-menu-item">
        <div>
          <div>
            {!menu && (
              <div className="page-menu-item__empty">
                <EmptyState
                  icon={() => <img src="/images/empty-menu.png" alt="empty menu" />}
                  title="Item not found!"
                  description="We couldn't find the item you are looking for."
                />
              </div>
            )}
            {menu && (
              <div className="page-menu-item__menu-item">
                <div className="page-menu-item__back" onClick={() => navigate('/')}>
                  <ArrowLeftIcon />
                  <span>Back</span>
                </div>
                <div className="page-menu-item__container">
                  {isMobile && (
                    <div className="page-menu-item__image-container">
                      <MenuItemGallery
                        images={[menu.imageUrl, menu.secondaryImageUrl]}
                        alt={`Menu Item ${menu.id}`}
                      />
                      {(!isInCart || hasOptions) && (
                        <div className="page-menu-item__quantity">
                          <Button className="page-menu-item__action-button" onClick={handleAddToCartClick}>
                            <CartIcon width={24} height={24} />
                            <span>ADD TO CART</span>
                          </Button>
                        </div>
                      )}
                      {isInCart && !hasOptions && (
                        <div className="page-menu-item__quantity">
                          <QuantitySelector
                            quantity={cartItem.quantity}
                            onIncrement={(e) => {
                              e?.stopPropagation?.();
                              incrementItem(menu);
                            }}
                            onDecrement={(e) => {
                              e?.stopPropagation?.();
                              decrementItem(menu);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  <div className="page-menu-item__content">
                    <div>
                      <span className="page-menu-item__name">{menu.name}</span>
                      <Currency className="page-menu-item__price" value={menu.price} />
                    </div>
                    <div className="page-menu-item__categories">
                      {menu.categories.map((category) => (
                        <span
                          className={`page-menu-item__tag ${getColorForKeyword(category.name, 'page-menu-item__tag')}`}
                        >
                          {category.name}
                        </span>
                      ))}
                    </div>
                    <p className="page-menu-item__description">{menu.description}</p>
                    {menu?.nutrition && <Nutrition nutrition={menu.nutrition} />}
                    {!menu?.nutrition && (
                      <div className="page-menu-item__info-container">
                        <NutritionIcons />
                        <div className="page-menu-item__info">
                          <span>Allergy Notice</span>
                          <p>
                            This meal is prepared in a kitchen that also handles gluten, wheat, soy,
                            eggs, dairy, nuts, peanuts, sesame seeds, shellfish, and other common
                            allergens. While every effort is made to avoid cross-contamination,
                            trace amounts may still be present.
                          </p>
                        </div>
                        <div className="page-menu-item__info">
                          <span>Disclaimer</span>
                          <p>
                            Instant Meals accepts no liability for any allergic reactions, health
                            issues, or damages arising from the consumption, improper storage, or
                            reheating of our products. Meals may be frozen if desired, but Instant
                            Meals is not liable for any change in taste, texture, or nutritional
                            quality once frozen.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  {!isMobile && (
                    <div className="page-menu-item__image-container">
                      <MenuItemGallery
                        images={[menu.imageUrl, menu.secondaryImageUrl]}
                        alt={`Menu Item ${menu.id}`}
                      />
                      {(!isInCart || hasOptions) && (
                        <div className="page-menu-item__quantity">
                          <Button className="page-menu-item__action-button" onClick={handleAddToCartClick}>
                            <CartIcon width={24} height={24} />
                            <span>ADD TO CART</span>
                          </Button>
                        </div>
                      )}
                      {isInCart && !hasOptions && (
                        <div className="page-menu-item__quantity">
                          <QuantitySelector
                            quantity={cartItem.quantity}
                            onIncrement={(e) => {
                              e?.stopPropagation?.();
                              incrementItem(menu);
                            }}
                            onDecrement={(e) => {
                              e?.stopPropagation?.();
                              decrementItem(menu);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {menu?.nutrition && (
                  <div className="page-menu-item__info-container">
                    <NutritionIcons />
                    <div className="page-menu-item__info">
                      <span>Allergy Notice</span>
                      <p>
                        This meal is prepared in a kitchen that also handles gluten, wheat, soy,
                        eggs, dairy, nuts, peanuts, sesame seeds, shellfish, and other common
                        allergens. While every effort is made to avoid cross-contamination, trace
                        amounts may still be present.
                      </p>
                    </div>
                    <div className="page-menu-item__info">
                      <span>Disclaimer</span>
                      <p>
                        Instant Meals accepts no liability for any allergic reactions, health
                        issues, or damages arising from the consumption, improper storage, or
                        reheating of our products. Meals may be frozen if desired, but Instant Meals
                        is not liable for any change in taste, texture, or nutritional quality once
                        frozen.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="page-menu-item__build">
            <FlameIcon />
            <div>
              <span>Build Your Meal, Build Your Body</span>
              <p>
                Our meals are more than convenience - they give you the power to take control of
                your nutrition.
              </p>
            </div>
            <Button className="page-menu-item__build-button" onClick={handleCreateMealClick}>
              <span>Build Your Meal</span>
            </Button>
          </div>
        </div>
        {!isMobile && <Sidebar />}
      </div>
      {hasOptions && (
        <MenuItemOptionsModal
          open={showOptionsModal}
          onOpen={setShowOptionsModal}
          item={menu}
          onConfirm={({ selectedOptionIds, selectedOptions, priceDelta }) => {
            addToCart({
              ...menu,
              basePrice: menu.price,
              price: menu.price + priceDelta,
              selectedOptionIds,
              selectedOptions
            });
          }}
        />
      )}
    </Navigation>
  );
};

export const MenuItemGallery = ({ images, alt }) => {
  const [active, setActive] = useState(0);
  const gallery = images.filter(Boolean);
  const [primaryImage, secondaryImage] = images;

  useEffect(() => {
    setActive(0);
  }, [primaryImage, secondaryImage]);

  return (
    <div className="page-menu-item__gallery">
      <div className="page-menu-item__image">
        <img src={gallery[active]} alt={alt} />
      </div>
      {gallery.length > 1 && (
        <div className="page-menu-item__gallery-thumbs">
          {gallery.map((src, index) => (
            <button
              key={src}
              type="button"
              className={`page-menu-item__gallery-thumb${index === active ? ' active' : ''}`}
              onClick={() => setActive(index)}
            >
              <img src={src} alt="" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const META_KEYS = ['servings', 'weight', 'servingSizeG'];

export const Nutrition = ({ nutrition }) => {
  // Build header text (servings, weight, serving size)
  const nutritionText = useMemo(() => {
    const servings = nutrition?.servings;
    const weight = nutrition?.weight;
    const servingSize = nutrition?.servingSizeG;

    const servingsText = servings
      ? servings > 1
        ? `${servings} servings`
        : 'Single Serving'
      : null;

    const weightText = weight ? `Total weight ${weight}g` : null;
    const servingSizeText = servingSize ? `Serving size ${servingSize}g` : null;

    return (
      [servingsText, weightText, servingSizeText].filter(Boolean).join(' | ') ||
      'Nutrition info not provided'
    );
  }, [nutrition]);

  // Separate into general items and sections, excluding zeros
  const { generalItems, sections } = useMemo(() => {
    const entries = Object.entries(nutrition || {}).filter(
      ([key, value]) =>
        !META_KEYS.includes(key) &&
        value != null &&
        value !== 0 &&
        (typeof value !== 'object' || Object.keys(value).length > 0)
    );

    // Top-level sections
    const sections = entries
      .filter(([_, value]) => typeof value === 'object')
      .map(([key, value]) => [key, Object.entries(value).filter(([_, v]) => v != null && v !== 0)])
      .filter(([_, items]) => items.length > 0);

    // Top-level items that are not objects
    const generalItems = entries.filter(([_, value]) => typeof value !== 'object');

    return { generalItems, sections };
  }, [nutrition]);

  // Accordion state
  const [openSection, setOpenSection] = useState('general');

  useEffect(() => {
    if (sections.length === 1) {
      setOpenSection(sections[0][0]);
    }
  }, [sections]);

  const extractUnit = (key) => {
    if (key?.endsWith('G')) return 'g';
    if (key?.endsWith('Mg')) return 'mg';
    if (key?.endsWith('Kcal')) return 'kcal';
    return null;
  };

  const formatLabel = (key) =>
    key
      .replace(/(G|Mg|Kcal)$/, '')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (c) => c.toUpperCase());

  const renderSectionItems = (items) =>
    items.map(([key, value]) => (
      <div key={key} className="page-menu-item__nutrition-item">
        <span>{formatLabel(key)}</span>
        <span>
          {value} {extractUnit(key)}
        </span>
      </div>
    ));

  const toggleSection = (key) => {
    setOpenSection((prev) => (prev === key ? null : key));
  };

  return (
    <div className="page-menu-item__nutrition">
      <div className="page-menu-item__nutrition-header">{nutritionText}</div>

      {generalItems.length > 0 && (
        <div key="general" className="page-menu-item__nutrition-section">
          <div
            className="page-menu-item__nutrition-section-title"
            onClick={() => toggleSection('general')}
            style={{ cursor: 'pointer' }}
          >
            General
          </div>
          {openSection === 'general' && (
            <div className="page-menu-item__nutrition-general-items">
              {renderSectionItems(generalItems)}
            </div>
          )}
        </div>
      )}

      {sections.map(([key, items]) => (
        <div key={key} className="page-menu-item__nutrition-section">
          <div
            className="page-menu-item__nutrition-section-title"
            onClick={() => toggleSection(key)}
            style={{ cursor: 'pointer' }}
          >
            {formatLabel(key)}
          </div>
          {openSection === key && (
            <div className="page-menu-item__nutrition-section-items">
              {renderSectionItems(items)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
